import PropTypes from 'prop-types';
import React, { memo, useContext } from 'react';
import { AppContext } from 'src/components/app-context';
import { SingleRange } from 'src/components/filters';

const MIN_RANGE_SIZE = 1;

const convertRating = rate => rate / 2;

const getPriceRangeData = data => {
  let min = data.aggregations.prices.min_price.value || 0;
  let max = data.aggregations.prices.max_price.value || 100;

  // Separate the min and max value if the difference is less than the given threshold
  if (max - min < MIN_RANGE_SIZE) {
    max += MIN_RANGE_SIZE * 3;
    if (min > 0) {
      min -= 1;
    }
  }

  min = Math.floor(min);
  max = Math.ceil(max);

  return {
    values: [
      {
        from: min,
        to: Math.round(max / 2),
        products: 0,
      },
      {
        from: Math.round(max / 2),
        to: max,
        products: 0,
      },
    ],
    min_value: min,
    max_value: max,
    max_matching_products: data.hits.total,
  };
};

const getRatingRangeData = ratings => ({
  values: ratings
    .map((agg, i, aggs) => ({
      from: aggs[i - 1] ? convertRating(aggs[i - 1].key) : 0,
      to: convertRating(agg.key),
      products: agg.doc_count,
    }))
    .filter(rating => rating.from !== rating.to),
  min_value: 0,
  max_value: 5,
  max_matching_products: Math.max(...ratings.map(r => r.doc_count)),
});

const getRangeData = (data, field) => {
  switch (field) {
    case 'storeRating': {
      return getRatingRangeData(data.aggregations.store_rating.bars.buckets);
    }
    case 'productRating': {
      return getRatingRangeData(data.aggregations.product_rating.bars.buckets);
    }
    case 'price': {
      return getPriceRangeData(data);
    }
    default: {
      return null;
    }
  }
};

const Ranges = props => {
  const { t } = useContext(AppContext);

  if (!props.data) return null;

  const { router, onChange, rangeOptions } = props;
  const filterRanges = {
    price: {
      from: props.data.aggregations.prices.min_price.value,
      to: props.data.aggregations.prices.max_price.value,
    },
    ...router.filterRanges,
  };

  const ranges = rangeOptions.map(option => {
    const title = t('campaigns', option.translation);

    return (
      <span key={option.key}>
        <h6>{title}</h6>
        <SingleRange
          id={option.key}
          key={option.key}
          type="decimal"
          showBars={option.showBars}
          values={getRangeData(props.data, option.key)}
          selected={filterRanges[option.key]}
          onChange={selected => {
            let value = '';
            if (selected.value) {
              // Move the slider to the closest 0.5
              const valueFrom = Math.round(selected.value.from * 2) / 2;
              const valueTo = Math.round(selected.value.to * 2) / 2;
              value = `${valueFrom}-${valueTo}`;
            }
            onChange({
              id: option.key,
              value,
              keep: selected.value,
              title,
            });
          }}
        />
      </span>
    );
  });

  return <span>{ranges}</span>;
};

const { string, func, object, shape, arrayOf, bool, number } = PropTypes;

Ranges.propTypes = {
  data: object,
  router: shape({
    location: shape({
      pathname: string,
      search: string,
    }),
  }).isRequired,
  rangeOptions: arrayOf(
    shape({
      key: string,
      field: string,
      translation: string,
      showBars: bool,
    })
  ).isRequired,
  onChange: func.isRequired,
  campaignId: number.isRequired,
};

Ranges.defaultProps = {
  data: undefined,
};

export default memo(Ranges);
